// --- Actions & Hooks
import {
  SHOW_SPINNER,
  SET_AUTH_USER,
  SET_AUTH_TOKEN,
  SET_AUTH_ROLE,
  SET_AUTH_ERROR,
  USER_LOGOUT,
  SET_SESSION_START,
} from "./constants";
// --- External Libraries
import decode from "jwt-decode";
// --- Utilities
import {
  getAuthToken,
  setCurrentUser,
} from "../../utils/localStorageService";
import { apiClientV1, tapApi } from "../../api";
import {
  parsePermissionTokens,
  PERMISSIONS_UPDATED_EVENT,
} from "../../utils/permissions";

/**
 * Checks whether a JWT token is expired.
 * @param {string} token - The JWT token to check
 * @returns {boolean} True if the token is expired or invalid
 */
export function isTokenExpired(token) {
  try {
    const decoded = decode(token);
    if (!decoded?.exp) {
      return false;
    }
    return decoded.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * Action to set the authentication user.
 *
 * This action is dispatched to set the authenticated user in the Redux store.
 * It takes a payload containing the user data.
 *
 * @param token The user token to be set.
 * @returns {Object} The action object with type and payload.
 **/
export const fetchUserPermissions = (token) => async (dispatch) => {
  if (token === "") {
    return;
  }

  try {
    dispatch({ type: SHOW_SPINNER, showSpinner: true });
    dispatch({ type: SET_AUTH_ERROR, error: "" });

    const decodedToken = decode(token);

    const normalizedPermissions = parsePermissionTokens(
      decodedToken.permissions,
    );
    localStorage.setItem("permissions", normalizedPermissions.join(","));
    localStorage.setItem("email", decodedToken.email);
    localStorage.setItem("firstname", decodedToken.firstname);
    localStorage.setItem("lastname", decodedToken.lastname);
    localStorage.setItem("token", token);
    localStorage.removeItem("tagSort");
    localStorage.removeItem("sectionSort");
    localStorage.removeItem("productSort");
    localStorage.removeItem("contactSort");
    localStorage.removeItem("titlesSort");

    setCurrentUser(decodedToken.email);

    try {
      const featureFlags = await tapApi.fetchFeatureFlags();
      localStorage.setItem("feature-flags", featureFlags.join(","));
    } catch (flagError) {
      console.error("Error fetching feature flags:", flagError);
      localStorage.setItem("feature-flags", "");
    }

    dispatch({ type: SET_AUTH_TOKEN, token });
    dispatch({ type: SET_AUTH_ERROR, error: "" });
    window.dispatchEvent(new Event(PERMISSIONS_UPDATED_EVENT));
  } catch (err) {
    if (import.meta.env.DEV) console.error("Auth error:", err);
    dispatch({ type: SET_AUTH_ERROR, error: err.message });
  } finally {
    dispatch({ type: SHOW_SPINNER, showSpinner: false });
  }
};

/**
 * Action to log out the user.
 *
 * This action is dispatched to log out the user from the application.
 *
 * It clears the authentication token, user information, and roles from the Redux store.
 * It also clears all local storage and session storage to ensure no stale data remains.
 *
 * @returns {function(*): void}
 */
export const logout = () => async (dispatch) => {
  const token = getAuthToken();

  localStorage.clear();
  sessionStorage.clear();

  dispatch({ type: SET_AUTH_USER, user: "" });
  dispatch({ type: SET_AUTH_TOKEN, token: "" });
  dispatch({ type: SET_AUTH_ROLE, roles: "" });
  dispatch({ type: SET_AUTH_ERROR, error: "" });
  dispatch({ type: USER_LOGOUT });
  dispatch({ type: SHOW_SPINNER, showSpinner: false });

  if (token && !isTokenExpired(token)) {
    try {
      await apiClientV1(token).post("/users/logout");
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) {
        console.error("Backend logout failed:", error);
      }
    }
  }
};

/**
 * Action to set the authentication token.
 *
 * This action is dispatched to set the authentication token in the Redux store.
 * It takes a token as a parameter.
 *
 * @param token
 * @returns {function(*): void}
 */
export const setAuthToken = (token) => (dispatch) => {
  dispatch({ type: SET_AUTH_TOKEN, token });
};

/**
 * Action to set the authentication role.
 *
 * This action is dispatched to set the authentication role in the Redux store.
 * It takes a role as a parameter.
 *
 * @param user
 * @returns {function(*): void}
 */
export const setAuthUser = (user) => (dispatch) => {
  dispatch({ type: SET_AUTH_USER, user });
};

/**
 * Action to set the authentication role.
 *
 * This action is dispatched to set the authentication role in the Redux store.
 * It takes a role as a parameter.
 *
 * @param dateTime
 * @returns {function(*): void}
 */
export const setSessionStart = (dateTime) => (dispatch) => {
  dispatch({ type: SET_SESSION_START, sessionStart: dateTime });
};

export const setAuthError = (error) => (dispatch) => {
  dispatch({ type: SET_AUTH_ERROR, error: error || "" });
};
